// The tiles this person last had, kept on the device so the portal opens with
// no signal and still links through to apps that work offline (#391). Kept per
// signed-in account: on a shared iPad one person never sees another's tiles.
// Tiles are discovery only; each app's own gate still decides who gets in.
import { isConnectionFailure } from '@higgins/utils';
import { getMe, type MeResponse } from './api';
import { msalInstance } from './msal';

/** /api/me, or the copy kept from last time when there's no connection. */
export type Me = MeResponse & {
  /** True when this is the copy kept on the device, not a fresh read. */
  offline?: boolean;
};

const KEY_PREFIX = 'portal-me:';

function storageKey(): string | null {
  const username = msalInstance.getActiveAccount()?.username;
  return username ? `${KEY_PREFIX}${username.toLowerCase()}` : null;
}

function keep(me: MeResponse): void {
  const key = storageKey();
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(me));
  } catch {
    // Storage full or blocked: next time offline there's just nothing to show.
  }
}

function kept(): MeResponse | null {
  const key = storageKey();
  if (!key) return null;
  try {
    const text = localStorage.getItem(key);
    return text ? (JSON.parse(text) as MeResponse) : null;
  } catch {
    return null;
  }
}

/** The tiles: fresh when there's signal (kept for next time), else the kept copy. */
export async function loadMe(): Promise<Me> {
  try {
    const me = await getMe();
    keep(me);
    return me;
  } catch (err) {
    if (!isConnectionFailure(err)) throw err;
    const copy = kept();
    if (copy) return { ...copy, offline: true };
    throw new Error("No signal, and your apps haven't been saved on this device yet. Open Higgins Apps once with signal.");
  }
}
